import React, { useState, useEffect } from "react";
import { getSettings, updateSettings } from "../../api/settingsAPI";

const THEMES = [
  {
    id: "classic",
    label: "Classic",
    desc: "Layout sidebar + tabs, dark minimal",
    preview: (
      <div className="w-full h-20 rounded-lg bg-[#0d0d10] border border-zinc-700 overflow-hidden flex">
        <div className="w-16 bg-zinc-900 border-r border-zinc-800 flex flex-col gap-1.5 p-2">
          <div className="h-2 bg-zinc-700 rounded w-10" />
          <div className="h-1.5 bg-zinc-800 rounded w-8" />
          <div className="h-1.5 bg-zinc-800 rounded w-9" />
          <div className="h-1.5 bg-zinc-800 rounded w-7" />
        </div>
        <div className="flex-1 p-2 flex flex-col gap-1.5">
          <div className="h-2 bg-blue-900/60 rounded w-16" />
          <div className="h-1.5 bg-zinc-800 rounded w-full" />
          <div className="h-1.5 bg-zinc-800 rounded w-3/4" />
          <div className="flex gap-1 mt-1">
            <div className="h-4 bg-indigo-600/60 rounded w-8 text-[6px] flex items-center justify-center text-white">About</div>
            <div className="h-4 bg-zinc-800 rounded w-8 text-[6px] flex items-center justify-center text-zinc-500">Skill</div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "modern",
    label: "Modern Dark",
    desc: "Full-page sections, gradient hero, purple accent",
    preview: (
      <div className="w-full h-20 rounded-lg overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900/30 to-slate-900 border border-purple-800/40 flex flex-col">
        <div className="h-8 bg-gradient-to-r from-purple-600/30 to-blue-600/30 border-b border-purple-800/30 flex items-center px-2 gap-1.5">
          <div className="w-2 h-2 rounded-full bg-purple-400/60" />
          <div className="h-1.5 bg-purple-300/40 rounded w-12" />
          <div className="ml-auto flex gap-1">
            <div className="h-1.5 bg-purple-400/40 rounded w-6" />
            <div className="h-1.5 bg-purple-400/40 rounded w-6" />
          </div>
        </div>
        <div className="flex-1 flex items-center px-3 gap-2">
          <div className="flex flex-col gap-1">
            <div className="h-2 bg-white/20 rounded w-16" />
            <div className="h-1.5 bg-purple-300/30 rounded w-12" />
            <div className="h-4 bg-gradient-to-r from-purple-500 to-blue-500 rounded w-10 mt-0.5" />
          </div>
          <div className="ml-auto w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/40 to-blue-500/40 border border-purple-500/30" />
        </div>
      </div>
    ),
  },
  {
    id: "terminal",
    label: "Terminal",
    desc: "Hacker CLI vibe, green-on-black, monospace",
    preview: (
      <div className="w-full h-20 rounded-lg overflow-hidden bg-black border border-green-500/40 flex flex-col font-mono">
        <div className="h-5 bg-[#0d130d] border-b border-green-500/20 flex items-center px-2 gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500/70" />
          <span className="w-1.5 h-1.5 rounded-full bg-yellow-500/70" />
          <span className="w-1.5 h-1.5 rounded-full bg-green-500/70" />
          <span className="ml-1.5 text-[6px] text-green-700">bash — ~/whoami</span>
        </div>
        <div className="flex-1 p-2 flex flex-col gap-0.5 justify-center">
          <p className="text-[7px] text-green-600">$ whoami</p>
          <p className="text-[8px] text-green-400 font-bold">Affan Haidar</p>
          <p className="text-[6px] text-green-500 tracking-tighter">[████████░░] nmap 80%</p>
          <p className="text-[6px] text-green-500 tracking-tighter">
            $ <span className="inline-block w-1 h-2 bg-green-400 align-middle animate-pulse" />
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "fabrica",
    label: "Fabrica",
    desc: "Editorial & clean, warm off-white, bold typography, orange accent",
    preview: (
      <div className="w-full h-20 rounded-lg overflow-hidden bg-[#FAFAF8] border border-stone-200 flex flex-col">
        <div className="h-7 bg-white border-b border-stone-200 flex items-center px-2.5 gap-1.5">
          <div className="font-black text-[8px] text-stone-900">AH<span style={{ color: "#E85D04" }}>.</span></div>
          <div className="ml-auto flex gap-1.5">
            <div className="h-1.5 bg-stone-300 rounded w-5" />
            <div className="h-1.5 bg-stone-200 rounded w-5" />
            <div className="h-1.5 bg-stone-200 rounded w-5" />
          </div>
        </div>
        <div className="flex-1 flex items-center px-2.5 gap-3">
          <div className="flex flex-col gap-1">
            <div className="h-3 bg-stone-900 rounded w-14 font-black" />
            <div className="h-1.5 bg-stone-300 rounded w-10" />
            <div className="h-4 rounded w-10 mt-0.5" style={{ backgroundColor: "#E85D04" }} />
          </div>
          <div className="ml-auto w-9 h-11 rounded-xl bg-stone-200 border-2 border-stone-300" />
        </div>
      </div>
    ),
  },
  {
    id: "aurora",
    label: "Aurora",
    desc: "Dark cinematic, green accent, animated blobs, glassmorphism",
    preview: (
      <div className="w-full h-20 rounded-lg overflow-hidden bg-[#020617] border border-[rgba(34,197,94,0.2)] flex flex-col relative">
        {/* Ambient blob */}
        <div className="absolute top-0 left-0 w-16 h-16 rounded-full opacity-30"
          style={{ background:"radial-gradient(circle, #22C55E 0%, transparent 70%)", filter:"blur(8px)" }} />
        {/* Nav */}
        <div className="h-6 bg-[rgba(2,6,23,0.8)] border-b border-[rgba(148,163,184,0.1)] flex items-center px-2 gap-1.5 relative z-10">
          <div className="font-bold text-[8px] text-white">AH<span className="text-[#22C55E]">.</span></div>
          <div className="ml-auto flex gap-1">
            <div className="h-1.5 bg-[rgba(34,197,94,0.4)] rounded w-5" />
            <div className="h-1.5 bg-[rgba(148,163,184,0.2)] rounded w-5" />
          </div>
        </div>
        {/* Hero */}
        <div className="flex-1 flex items-center px-2.5 gap-2 relative z-10">
          <div className="flex flex-col gap-1">
            <div className="h-2.5 bg-white/20 rounded w-14" />
            <div className="h-1.5 bg-[#22C55E]/60 rounded w-10" />
            <div className="flex gap-1 mt-0.5">
              <div className="h-3 rounded w-8 text-[5px] flex items-center justify-center font-bold" style={{ background:"#22C55E", color:"#020617" }}>Hire</div>
              <div className="h-3 rounded w-8 text-[5px] flex items-center justify-center border border-white/20 text-white/50">Work</div>
            </div>
          </div>
          <div className="ml-auto w-9 h-9 rounded-full border border-[rgba(34,197,94,0.4)] bg-[rgba(34,197,94,0.1)]" />
        </div>
      </div>
    ),
  },
];

const ThemeSwitcher = () => {
  const [activeTheme, setActiveTheme] = useState("classic");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSettings()
      .then((s) => setActiveTheme(s.active_theme ?? "classic"))
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = async (themeId) => {
    if (themeId === activeTheme || saving) return;
    setSaving(true);
    try {
      const result = await updateSettings({ active_theme: themeId });
      setActiveTheme(result.active_theme ?? themeId);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-zinc-900/40 border border-zinc-800 p-6 rounded-2xl">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-1 w-8 bg-violet-500 rounded-full" />
        <span className="text-[10px] font-black uppercase tracking-[0.3em] text-violet-400">Appearance</span>
      </div>
      <h3 className="text-lg font-black text-white mb-1">
        TEMA <span className="text-violet-400">FRONTEND</span>
      </h3>
      <p className="text-xs text-zinc-500 mb-5">
        Pilih tampilan halaman publik. Perubahan langsung berlaku saat visitor reload.
      </p>

      {loading ? (
        <div className="flex gap-1 items-center text-zinc-600 text-sm">
          <span className="w-1.5 h-1.5 bg-zinc-600 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-1.5 h-1.5 bg-zinc-600 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="w-1.5 h-1.5 bg-zinc-600 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {THEMES.map((theme) => {
            const isActive = activeTheme === theme.id;
            return (
              <button
                key={theme.id}
                onClick={() => handleSelect(theme.id)}
                disabled={saving}
                className={`text-left p-3 rounded-xl border-2 transition-all duration-200 ${
                  isActive
                    ? "border-violet-500 bg-violet-500/10"
                    : "border-zinc-700 bg-zinc-800/40 hover:border-zinc-500"
                } ${saving ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <div className="mb-3">{theme.preview}</div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-white">{theme.label}</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">{theme.desc}</p>
                  </div>
                  {isActive && (
                    <div className="shrink-0 ml-2 flex items-center gap-1 bg-violet-500/20 text-violet-400 text-[10px] font-bold px-2 py-1 rounded-lg border border-violet-500/30">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414L8.414 15l-5.121-5.121a1 1 0 011.414-1.414L8.414 12.172l6.879-6.879a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Aktif
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {saving && (
        <p className="text-xs text-violet-400 mt-3 animate-pulse">Saving theme changes...</p>
      )}
    </div>
  );
};

export default ThemeSwitcher;
